import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import WarningIcon from '../../assets/icons/WarningIcon';
import Modal from '../../components/Modal';
import Dropdown from '../../components/Dropdown';
import { useTranslation } from '../../contexts/TranslationContext';
import '../../styles/manage-recommendations.scss';
import { TipsterTier } from '../../types/interfaces';
import { apiGet, apiPost } from '../../utils/api';
import { Icon } from '../../utils/Icon';
import { translateEvent } from '../../utils/translateEvent';

/** Free tier (`level === 0`); otherwise first tier in list. */
function defaultFreeTierId(tiersList: TipsterTier[]): number | null {
  if (tiersList.length === 0) return null;
  return tiersList.find((t) => t.level === 0)?.id ?? tiersList[0].id;
}

interface Sport {
  id: number;
  name: string;
  icon?: string | null;
}

interface League {
  id: number;
  name: string;
  sport_id: number;
}

interface Game {
  id: number;
  datetime: string;
  home_team: string;
  away_team: string;
  sport_id: number;
  league_id: number;
  sport?: Sport;
  league?: League;
}

interface BetEventMarketGroup {
  id: number;
  odds_api_id?: string;
  order: number;
  odds_api_name?: string;
  translated_name?: string;
  download: boolean;
}

interface BetEvent {
  id: number;
  odds: number;
  event: string;
  game_id: number;
  game?: Game;
  category_name?: string;
  category_id?: string;
  bet_event_market_group_id?: number;
  event_order?: number;
  bet_event_market_group?: BetEventMarketGroup;
  specifiers?: Record<string, string> | null;
  line_value?: number | null;
  outcome_side?: 'under' | 'over' | null;
  market_code?: string | null;
}

interface MarketGroupWithEvents {
  bet_event_market_group: BetEventMarketGroup | null;
  bet_event_market_group_id: number | null;
  events: BetEvent[];
}

interface BetEventsByGameResponse {
  market_groups: MarketGroupWithEvents[];
}

const STAKE_STEPS = [0.25, 0.5, 1, 1.5, 2, 3, 5, 10];
const DEFAULT_STAKE_IDX = 2;

const StakeSlider: React.FC<{ value: number; onChange: (idx: number) => void }> = ({ value, onChange }) => (
  <div className="stake-slider">
    <div className="stake-slider__track" />
    <div className="stake-slider__dots">
      {STAKE_STEPS.map((step, idx) => (
        <button
          key={step}
          type="button"
          className={`stake-slider__step${idx === value ? ' stake-slider__step--active' : ''}`}
          onClick={() => onChange(idx)}
        >
          <span className="stake-slider__dot" />
          <span className="stake-slider__label">{step}</span>
        </button>
      ))}
    </div>
  </div>
);

interface RecommendationForm {
  tipster_tier_id: number | null;
  stakeIdx: number;
  tipster_description: string;
}

const AddRecommendation: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [sports, setSports] = useState<Sport[]>([]);
  const [leagues, setLeagues] = useState<League[]>([]);
  const [games, setGames] = useState<Game[]>([]);
  const [betEvents, setBetEvents] = useState<BetEvent[]>([]);
  const [popularGames, setPopularGames] = useState<Game[]>([]);
  const [tiers, setTiers] = useState<TipsterTier[]>([]);

  const [selectedSport, setSelectedSport] = useState<number | null>(null);
  const [selectedLeague, setSelectedLeague] = useState<number | null>(null);
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [loadingSports, setLoadingSports] = useState(true);
  const [loadingLeagues, setLoadingLeagues] = useState(false);
  const [loadingGames, setLoadingGames] = useState(false);
  const [loadingBetEvents, setLoadingBetEvents] = useState(false);
  const [loadingPopular, setLoadingPopular] = useState(true);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedBetEvent, setSelectedBetEvent] = useState<BetEvent | null>(null);
  const [formData, setFormData] = useState<RecommendationForm>({
    tipster_tier_id: null,
    stakeIdx: DEFAULT_STAKE_IDX,
    tipster_description: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [activeMarketGroupKey, setActiveMarketGroupKey] = useState<string | null>(null);
  const [marketGroupTabOrder, setMarketGroupTabOrder] = useState<string[]>([]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [sportsData, popularData, tiersData] = await Promise.all([
          apiGet<Sport[]>('/sports/'),
          apiGet<Game[]>('/games/popular?limit=5'),
          apiGet<TipsterTier[]>('/tipsters/me/tiers'),
        ]);
        setSports(sportsData);
        setPopularGames(popularData);
        setTiers(tiersData);
        if (tiersData.length > 0) {
          setFormData(prev => ({ ...prev, tipster_tier_id: defaultFreeTierId(tiersData) }));
        }
      } catch (err) {
        console.error('Failed to fetch initial data:', err);
      } finally {
        setLoadingSports(false);
        setLoadingPopular(false);
      }
    };
    fetchInitialData();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (!selectedSport) {
      setLeagues([]);
      setSelectedLeague(null);
      return;
    }

    const fetchLeagues = async () => {
      setLoadingLeagues(true);
      try {
        const data = await apiGet<League[]>(`/leagues/?sport_id=${selectedSport}&has_upcoming_games=true`);
        setLeagues(data);
      } catch (err) {
        console.error('Failed to fetch leagues:', err);
      } finally {
        setLoadingLeagues(false);
      }
    };
    fetchLeagues();
  }, [selectedSport]);

  const fetchGames = useCallback(async () => {
    if (!selectedSport) return;

    setLoadingGames(true);
    try {
      const params = new URLSearchParams();
      params.append('sport_id', selectedSport.toString());
      if (selectedLeague) params.append('league_id', selectedLeague.toString());
      if (debouncedSearch) params.append('search', debouncedSearch);
      params.append('limit', '20');

      const data = await apiGet<Game[]>(`/games/search?${params}`);
      setGames(data);
    } catch (err) {
      console.error('Failed to fetch games:', err);
    } finally {
      setLoadingGames(false);
    }
  }, [selectedSport, selectedLeague, debouncedSearch]);

  useEffect(() => {
    if (selectedSport) {
      fetchGames();
    } else {
      setGames([]);
    }
  }, [selectedSport, selectedLeague, debouncedSearch, fetchGames]);

  const handleSelectGame = async (game: Game) => {
    setSelectedGame(game);
    setLoadingBetEvents(true);
    try {
      const response = await apiGet<BetEventsByGameResponse>(`/bet-events/by-game/${game.id}`);

      const allEvents: BetEvent[] = [];
      response.market_groups.forEach(mg => {
        allEvents.push(...mg.events);
      });
      setBetEvents(allEvents);

      const tabOrder = response.market_groups.map((mg) =>
        mg.bet_event_market_group_id != null ? String(mg.bet_event_market_group_id) : 'no-group'
      );
      setMarketGroupTabOrder(tabOrder);

      const groupedByMarketGroup: Record<string, { marketGroup?: BetEventMarketGroup; events: BetEvent[] }> = {};
      response.market_groups.forEach((mg) => {
        const groupId =
          mg.bet_event_market_group_id != null ? String(mg.bet_event_market_group_id) : 'no-group';
        groupedByMarketGroup[groupId] = {
          marketGroup: mg.bet_event_market_group || undefined,
          events: mg.events,
        };
      });

      const initialExpanded = new Set<string>();
      let categoryCount = 0;

      for (const groupId of tabOrder) {
        const { events } = groupedByMarketGroup[groupId];
        const categorySet = new Set<string>();
        for (const betEvent of events) {
          const categoryId = betEvent.category_id || 'other';
          if (!categorySet.has(categoryId)) {
            categorySet.add(categoryId);
            if (categoryCount < 3) {
              initialExpanded.add(`${groupId}-${categoryId}`);
              categoryCount++;
            }
          }
        }
      }

      setExpandedCategories(initialExpanded);
      setActiveMarketGroupKey(tabOrder[0] ?? null);
    } catch (err) {
      console.error('Failed to fetch bet events:', err);
      setActiveMarketGroupKey(null);
      setMarketGroupTabOrder([]);
    } finally {
      setLoadingBetEvents(false);
    }
  };

  const handleBackToGames = () => {
    setSelectedGame(null);
    setBetEvents([]);
    setActiveMarketGroupKey(null);
    setMarketGroupTabOrder([]);
  };

  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const marketGroupsForGame = useMemo(() => {
    const groupedByMarketGroup = betEvents.reduce(
      (acc, betEvent) => {
        const marketGroupId =
          betEvent.bet_event_market_group_id != null
            ? String(betEvent.bet_event_market_group_id)
            : 'no-group';
        if (!acc[marketGroupId]) {
          acc[marketGroupId] = {
            marketGroup: betEvent.bet_event_market_group,
            events: [],
          };
        }
        acc[marketGroupId].events.push(betEvent);
        return acc;
      },
      {} as Record<string, { marketGroup?: BetEventMarketGroup; events: BetEvent[] }>
    );

    let sortedMarketGroupIds: string[];
    if (marketGroupTabOrder.length > 0) {
      const seen = new Set<string>();
      sortedMarketGroupIds = [];
      for (const key of marketGroupTabOrder) {
        if (groupedByMarketGroup[key] && !seen.has(key)) {
          seen.add(key);
          sortedMarketGroupIds.push(key);
        }
      }
      for (const key of Object.keys(groupedByMarketGroup)) {
        if (!seen.has(key)) sortedMarketGroupIds.push(key);
      }
    } else {
      sortedMarketGroupIds = Object.keys(groupedByMarketGroup).sort((a, b) => {
        if (a === 'no-group') return 1;
        if (b === 'no-group') return -1;
        const groupA = groupedByMarketGroup[a].marketGroup;
        const groupB = groupedByMarketGroup[b].marketGroup;
        if (!groupA) return 1;
        if (!groupB) return -1;
        return groupA.order - groupB.order;
      });
    }

    return { groupedByMarketGroup, sortedMarketGroupIds };
  }, [betEvents, marketGroupTabOrder]);

  const effectiveMarketGroupKey =
    activeMarketGroupKey && marketGroupsForGame.groupedByMarketGroup[activeMarketGroupKey]
      ? activeMarketGroupKey
      : marketGroupsForGame.sortedMarketGroupIds[0] ?? null;

  const marketTabLabel = (groupId: string, marketGroup?: BetEventMarketGroup) => {
    if (groupId === 'no-group') {
      return t.manageRecommendations.otherTab;
    }
    const name = marketGroup?.translated_name?.trim();
    if (name) return name;
    return t.manageRecommendations.untitledMarketGroup ?? '—';
  };

  const handleAddRecommendation = (betEvent: BetEvent) => {
    setSelectedBetEvent(betEvent);
    setFormData({
      tipster_tier_id: defaultFreeTierId(tiers),
      stakeIdx: DEFAULT_STAKE_IDX,
      tipster_description: '',
    });
    setSubmitError(null);
    setIsAddModalOpen(true);
  };

  const handleSubmitRecommendation = async () => {
    if (!selectedBetEvent) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await apiPost('/tipsters/me/recommendations', {
        bet_event_id: selectedBetEvent.id,
        tipster_tier_id: formData.tipster_tier_id,
        stake: STAKE_STEPS[formData.stakeIdx],
        tipster_description: formData.tipster_description || null,
      });

      setIsAddModalOpen(false);
      setSelectedBetEvent(null);
    } catch (err: any) {
      setSubmitError(t.manageRecommendations.submitError);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setIsAddModalOpen(false);
    setSelectedBetEvent(null);
    setSubmitError(null);
  };

  if (selectedGame) {
    return (
      <>
      <div className="manage-recs">
        <div className="manage-recs__container">
          <button className="manage-recs__back" onClick={handleBackToGames}>
            ← {t.manageRecommendations.backToGames}
          </button>

          <div className="manage-recs__game-header">
            <h2 className="manage-recs__game-title">
              {translateEvent(selectedGame.home_team, t.eventsDictionary)} vs {translateEvent(selectedGame.away_team, t.eventsDictionary)}
            </h2>
            <span className="manage-recs__game-meta">
              {selectedGame.league?.name ? translateEvent(selectedGame.league.name, t.eventsDictionary) : ''} • {formatDateTime(selectedGame.datetime)}
            </span>
          </div>

          <h3 className="manage-recs__section-title">
            {t.manageRecommendations.availableBets}
          </h3>

          {loadingBetEvents ? (
            <div className="manage-recs__loading">{t.common.loading}</div>
          ) : betEvents.length === 0 ? (
            <div className="manage-recs__empty">
              {t.manageRecommendations.noBetsAvailable}
            </div>
          ) : (
              <div className="manage-recs__bets-container">
                <div className="manage-recs__market-tabs" role="tablist" aria-label={t.manageRecommendations.availableBets}>
                  {marketGroupsForGame.sortedMarketGroupIds.map((groupId) => {
                    const { marketGroup } = marketGroupsForGame.groupedByMarketGroup[groupId];
                    const isActive = effectiveMarketGroupKey === groupId;
                    return (
                      <button
                        key={groupId}
                        type="button"
                        role="tab"
                        id={`manage-recs-market-tab-${groupId}`}
                        className={`manage-recs__market-tab${isActive ? ' manage-recs__market-tab--active' : ''}`}
                        aria-selected={isActive}
                        tabIndex={isActive ? 0 : -1}
                        onClick={() => setActiveMarketGroupKey(groupId)}
                      >
                        {marketTabLabel(groupId, marketGroup)}
                      </button>
                    );
                  })}
                </div>
                {effectiveMarketGroupKey &&
                  (() => {
                    const bucket = marketGroupsForGame.groupedByMarketGroup[effectiveMarketGroupKey];
                    if (!bucket) return null;
                    const { events } = bucket;
                    const groupId = effectiveMarketGroupKey;

                    const groupedByCategory = events.reduce(
                      (acc, betEvent, index) => {
                        const categoryId = betEvent.category_id || 'other';
                        if (!acc[categoryId]) {
                          acc[categoryId] = {
                            categoryName: betEvent.category_name || t.manageRecommendations.otherTab,
                            events: [],
                            firstIndex: index,
                          };
                        }
                        acc[categoryId].events.push(betEvent);
                        return acc;
                      },
                      {} as Record<string, { categoryName: string; events: BetEvent[]; firstIndex: number }>
                    );

                    const sortedCategoryIds = Object.keys(groupedByCategory).sort((a, b) => {
                      if (a === 'other') return 1;
                      if (b === 'other') return -1;
                      return groupedByCategory[a].firstIndex - groupedByCategory[b].firstIndex;
                    });

                    return (
                      <div
                        role="tabpanel"
                        id="manage-recs-market-panel"
                        aria-labelledby={`manage-recs-market-tab-${groupId}`}
                        className="manage-recs__market-tabpanel"
                      >
                        {sortedCategoryIds.map((categoryId) => {
                          const { categoryName, events: categoryEvents } = groupedByCategory[categoryId];
                          const categoryKey = `${groupId}-${categoryId}`;
                          const isExpanded = expandedCategories.has(categoryKey);

                          const lineTableEvents = categoryEvents.filter(
                            (e) => e.line_value != null && (e.outcome_side === 'under' || e.outcome_side === 'over')
                          );
                          const isLineTable = lineTableEvents.length > 0;
                          const leftoverEvents = categoryEvents.filter((e) => !lineTableEvents.includes(e));

                          const rowsByLine = new Map<number, { under?: BetEvent; over?: BetEvent }>();
                          if (isLineTable) {
                            for (const e of lineTableEvents) {
                              const row = rowsByLine.get(e.line_value as number) ?? {};
                              row[e.outcome_side as 'under' | 'over'] = e;
                              rowsByLine.set(e.line_value as number, row);
                            }
                          }
                          const sortedLines = Array.from(rowsByLine.keys()).sort((a, b) => a - b);

                          const handicapEntries = !isLineTable
                            ? categoryEvents.reduce<{ betEvent: BetEvent; team: string; value: number }[]>((acc, e) => {
                                if (e.line_value == null) return acc;
                                const name = e.event.slice(categoryName.length + 3);
                                const match = name.match(/^(.+)\s\(([+-]?\d+(?:\.\d+)?)\)$/);
                                if (!match) return acc;
                                acc.push({ betEvent: e, team: match[1].trim(), value: parseFloat(match[2]) });
                                return acc;
                              }, [])
                            : [];
                          const handicapTeams = Array.from(new Set(handicapEntries.map((e) => e.team)));
                          const isHandicapTable = handicapTeams.length === 2;
                          let orderedTeams = handicapTeams;
                          if (isHandicapTable && selectedGame) {
                            const normalize = (s: string) => s.trim().toLowerCase();
                            const homeMatch = handicapTeams.find((tm) => normalize(tm) === normalize(selectedGame.home_team));
                            const awayMatch = handicapTeams.find((tm) => normalize(tm) === normalize(selectedGame.away_team));
                            if (homeMatch && awayMatch) orderedTeams = [homeMatch, awayMatch];
                          }
                          const handicapRowsByLine = new Map<number, Record<string, { value: number; betEvent: BetEvent }>>();
                          if (isHandicapTable) {
                            for (const entry of handicapEntries) {
                              const row = handicapRowsByLine.get(entry.betEvent.line_value as number) ?? {};
                              row[entry.team] = { value: entry.value, betEvent: entry.betEvent };
                              handicapRowsByLine.set(entry.betEvent.line_value as number, row);
                            }
                          }
                          const sortedHandicapLines = Array.from(handicapRowsByLine.keys()).sort((a, b) => a - b);
                          const handicapEventIds = new Set(handicapEntries.map((e) => e.betEvent.id));
                          const handicapLeftoverEvents = isHandicapTable
                            ? categoryEvents.filter((e) => !handicapEventIds.has(e.id))
                            : [];

                          const toggleCategory = () => {
                            setExpandedCategories((prev) => {
                              const next = new Set(prev);
                              if (next.has(categoryKey)) {
                                next.delete(categoryKey);
                              } else {
                                next.add(categoryKey);
                              }
                              return next;
                            });
                          };

                          return (
                            <div key={categoryKey} className="manage-recs__category-group">
                              <div
                                className="manage-recs__category-header"
                                onClick={toggleCategory}
                                style={{ cursor: 'pointer' }}
                              >
                                <span className="manage-recs__category-name">
                                  {translateEvent(categoryName, t.eventsDictionary)}
                                </span>
                                <Icon
                                  component={isExpanded ? FiChevronUp : FiChevronDown}
                                  className="manage-recs__category-chevron"
                                />
                              </div>
                              {isExpanded && (
                                isLineTable ? (
                                  <>
                                    <div className="manage-recs__line-table" role="table">
                                      <div className="manage-recs__line-table-header" role="row">
                                        <span className="manage-recs__line-table-cell manage-recs__line-table-cell--label" />
                                        <span className="manage-recs__line-table-cell manage-recs__line-table-cell--head">
                                          {t.manageRecommendations.under}
                                        </span>
                                        <span className="manage-recs__line-table-cell manage-recs__line-table-cell--head">
                                          {t.manageRecommendations.over}
                                        </span>
                                      </div>
                                      {sortedLines.map((lineValue) => {
                                        const row = rowsByLine.get(lineValue);
                                        return (
                                          <div key={lineValue} className="manage-recs__line-table-row" role="row">
                                            <span className="manage-recs__line-table-cell manage-recs__line-table-cell--label">
                                              {lineValue}
                                            </span>
                                            {(['under', 'over'] as const).map((side) => {
                                              const betEvent = row?.[side];
                                              return (
                                                <div key={side} className="manage-recs__line-table-cell manage-recs__line-table-cell--outcome">
                                                  {betEvent ? (
                                                    <button
                                                      className="manage-recs__line-table-btn"
                                                      onClick={() => handleAddRecommendation(betEvent)}
                                                    >
                                                      <span className="manage-recs__line-table-odds">
                                                        {betEvent.odds.toFixed(2)}
                                                      </span>
                                                    </button>
                                                  ) : (
                                                    <span className="manage-recs__line-table-empty">—</span>
                                                  )}
                                                </div>
                                              );
                                            })}
                                          </div>
                                        );
                                      })}
                                    </div>
                                    {leftoverEvents.length > 0 && (
                                      <div className="manage-recs__bets-grid">
                                        {leftoverEvents.map((betEvent) => (
                                          <div key={betEvent.id} className="manage-recs__bet-card">
                                            <div className="manage-recs__bet-info">
                                              <span className="manage-recs__bet-event">
                                                {translateEvent(betEvent.event, t.eventsDictionary)}
                                              </span>
                                              <span className="manage-recs__bet-odds">{betEvent.odds.toFixed(2)}</span>
                                            </div>
                                            <button
                                              className="manage-recs__add-btn"
                                              onClick={() => handleAddRecommendation(betEvent)}
                                            >
                                              + {t.manageRecommendations.addRecommendation}
                                            </button>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </>
                                ) : isHandicapTable ? (
                                  <>
                                    <div className="manage-recs__handicap-table" role="table">
                                      <div className="manage-recs__handicap-table-header" role="row">
                                        {orderedTeams.map((team) => (
                                          <span
                                            key={team}
                                            className="manage-recs__handicap-table-cell manage-recs__handicap-table-cell--head"
                                          >
                                            {translateEvent(team, t.eventsDictionary)}
                                          </span>
                                        ))}
                                      </div>
                                      {sortedHandicapLines.map((lineValue) => {
                                        const row = handicapRowsByLine.get(lineValue);
                                        return (
                                          <div key={lineValue} className="manage-recs__handicap-table-row" role="row">
                                            {orderedTeams.map((team) => {
                                              const cell = row?.[team];
                                              return (
                                                <div
                                                  key={team}
                                                  className="manage-recs__handicap-table-cell manage-recs__handicap-table-cell--outcome"
                                                >
                                                  {cell ? (
                                                    <button
                                                      className="manage-recs__handicap-table-btn"
                                                      onClick={() => handleAddRecommendation(cell.betEvent)}
                                                    >
                                                      <span className="manage-recs__handicap-table-value">{cell.value}</span>
                                                      <span className="manage-recs__handicap-table-odds">
                                                        {cell.betEvent.odds.toFixed(2)}
                                                      </span>
                                                    </button>
                                                  ) : (
                                                    <span className="manage-recs__handicap-table-empty">—</span>
                                                  )}
                                                </div>
                                              );
                                            })}
                                          </div>
                                        );
                                      })}
                                    </div>
                                    {handicapLeftoverEvents.length > 0 && (
                                      <div className="manage-recs__bets-grid">
                                        {handicapLeftoverEvents.map((betEvent) => (
                                          <div key={betEvent.id} className="manage-recs__bet-card">
                                            <div className="manage-recs__bet-info">
                                              <span className="manage-recs__bet-event">
                                                {translateEvent(betEvent.event, t.eventsDictionary)}
                                              </span>
                                              <span className="manage-recs__bet-odds">{betEvent.odds.toFixed(2)}</span>
                                            </div>
                                            <button
                                              className="manage-recs__add-btn"
                                              onClick={() => handleAddRecommendation(betEvent)}
                                            >
                                              + {t.manageRecommendations.addRecommendation}
                                            </button>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <div className="manage-recs__bets-grid">
                                    {categoryEvents.map((betEvent) => (
                                      <div key={betEvent.id} className="manage-recs__bet-card">
                                        <div className="manage-recs__bet-info">
                                          <span className="manage-recs__bet-event">
                                            {translateEvent(betEvent.event, t.eventsDictionary)}
                                          </span>
                                          <span className="manage-recs__bet-odds">{betEvent.odds.toFixed(2)}</span>
                                        </div>
                                        <button
                                          className="manage-recs__add-btn"
                                          onClick={() => handleAddRecommendation(betEvent)}
                                        >
                                          + {t.manageRecommendations.addRecommendation}
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
              </div>
            )}
        </div>
      </div>
      <Modal
        isOpen={isAddModalOpen}
        onClose={handleCloseModal}
        title={
          <span className="manage-recs__modal-title-with-warning">
            {t.manageRecommendations.addRecommendationTitle}
            <span
              className="manage-recs__warning-tooltip-wrapper"
              data-tooltip={t.manageRecommendations.addWarning}
            >
              <WarningIcon className="manage-recs__modal-title-warning-icon" size={18} />
            </span>
          </span>
        }
        size="medium"
      >
        <div className="manage-recs__modal-content">
          <div className="manage-recs__modal-content-left">
            <div className="manage-recs-modal-summary">
              <div className="manage-recs-modal-summary__headline">
                <span className="manage-recs-modal-summary__event-title">
                  {selectedBetEvent
                    ? translateEvent(selectedBetEvent.event, t.eventsDictionary)
                    : '—'}
                </span>
              </div>
              <div className="manage-recs-modal-summary__fixture-match">
                <span className="manage-recs-modal-summary__teams">
                  <span className="manage-recs-modal-summary__team">{selectedGame?.home_team ? translateEvent(selectedGame.home_team, t.eventsDictionary) : ''}</span>
                  <span className="manage-recs-modal-summary__vs">vs</span>
                  <span className="manage-recs-modal-summary__team">{selectedGame?.away_team ? translateEvent(selectedGame.away_team, t.eventsDictionary) : ''}</span>
                </span>
              </div>
              <div className="manage-recs-modal-summary__odds-wrap">
                <span className="manage-recs-modal-summary__odds">
                  {selectedBetEvent?.odds != null ? selectedBetEvent.odds.toFixed(2) : '—'}
                </span>
              </div>
              {selectedGame?.league?.name ? (
                <div className="manage-recs-modal-summary__league">{translateEvent(selectedGame.league.name, t.eventsDictionary)}</div>
              ) : null}
              <div className="manage-recs-modal-summary__meta">
                <span className="manage-recs-modal-summary__date">
                  {formatDateTime(selectedGame?.datetime || '')}
                </span>
              </div>
            </div>
          </div>
          <div className="manage-recs__modal-content-right">
            <div>
              <p>{t.manageRecommendations.tierLabel}</p>
              <select
                value={formData.tipster_tier_id || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  tipster_tier_id: e.target.value ? Number(e.target.value) : null
                }))}

              >
                {tiers.length === 0 ? (
                  <option value="">{t.manageRecommendations.noTiers}</option>
                ) : (
                  tiers.map((tier) => (
                    <option key={tier.id} value={tier.id}>
                      {tier.name || `Tier ${tier.level}`}
                    </option>
                  ))
                )}
              </select>
            </div>
            <div>
              <p className="stake-slider__field-label">
                <span>{t.manageRecommendations.stakeLabel}</span>
                <span className="stake-slider__field-value">{STAKE_STEPS[formData.stakeIdx]}u</span>
              </p>
              <StakeSlider
                value={formData.stakeIdx}
                onChange={(idx) => setFormData(prev => ({ ...prev, stakeIdx: idx }))}
              />
            </div>
            <div>
              <p>{t.manageRecommendations.descriptionLabel}</p>
              <textarea
                placeholder={t.manageRecommendations.descriptionPlaceholder}
                rows={4}
                value={formData.tipster_description}
                onChange={(e) => setFormData(prev => ({ ...prev, tipster_description: e.target.value }))}
              />
            </div>
            {submitError && (
              <div className="manage-recs__error">{submitError}</div>
            )}
            <div>
              <button
                className="button_primary"
                onClick={handleSubmitRecommendation}
                disabled={isSubmitting || tiers.length === 0}
              >
                {isSubmitting ? t.common.loading : t.common.save}
              </button>
            </div>
          </div>
        </div>
      </Modal>
      </>
    );
  }

  return (
    <div className="manage-recs">
      <div className="manage-recs__container">
        <button className="button_primary" onClick={() => navigate(-1)}>
          {t.manageRecommendations.backToRecommendations}
        </button>

        <h1 className="manage-recs__title italic-title">{t.manageRecommendations.addRecommendationTitle}</h1>

        <section className="manage-recs__popular">
          <h2 className="manage-recs__section-title">
            {t.manageRecommendations.popularGames || '⭐ Popular Games'}
          </h2>
          {loadingPopular ? (
            <div className="manage-recs__loading">{t.common.loading}</div>
          ) : popularGames.length === 0 ? (
            <div className="manage-recs__empty">
              {t.manageRecommendations.noPopularGames}
            </div>
          ) : (
            <div className="manage-recs__popular-grid">
              {popularGames.map((game) => (
                <div
                  key={game.id}
                  className="manage-recs__popular-card"
                  onClick={() => handleSelectGame(game)}
                >
                  <div className="manage-recs__popular-teams">
                    <span>{translateEvent(game.home_team, t.eventsDictionary)}</span>
                    <span className="manage-recs__vs">vs</span>
                    <span>{translateEvent(game.away_team, t.eventsDictionary)}</span>
                  </div>
                  <div className="manage-recs__popular-meta">
                    <span className="manage-recs__popular-league">{game.league?.name ? translateEvent(game.league.name, t.eventsDictionary) : ''}</span>
                    <span className="manage-recs__popular-time">{formatDateTime(game.datetime)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <div className="manage-recs__divider">
          <span>{t.manageRecommendations.orSearch}</span>
        </div>

        <section className="manage-recs__search">
          <div className="manage-recs__filters">
            <div className="manage-recs__filter-group">
              <label className="manage-recs__label">{t.filters.sport}</label>
              <Dropdown
                className="manage-recs__select"
                value={selectedSport != null ? String(selectedSport) : ''}
                onChange={(sportValue) => {
                  setSelectedSport(sportValue ? Number(sportValue) : null);
                  setSelectedLeague(null);
                  setSearchTerm('');
                }}
                placeholder={loadingSports ? t.filters.loadingSports : t.filters.selectSport}
                title={t.filters.sport}
                disabled={loadingSports}
                options={
                  loadingSports
                    ? [{ value: '', label: t.filters.loadingSports, disabled: true }]
                    : [
                        { value: '', label: t.filters.selectSport },
                        ...sports.map((sport) => ({
                          value: String(sport.id),
                          label: t.sports[sport.name] ?? sport.name,
                          icon: sport.icon,
                        })),
                      ]
                }
              />
            </div>

            <div className="manage-recs__filter-group">
              <label className="manage-recs__label">{t.filters.league}</label>
              <Dropdown
                className="manage-recs__select"
                value={selectedLeague != null ? String(selectedLeague) : ''}
                onChange={(leagueValue) =>
                  setSelectedLeague(leagueValue ? Number(leagueValue) : null)
                }
                placeholder={
                  !selectedSport
                    ? t.filters.selectSportFirst
                    : loadingLeagues
                      ? t.filters.loadingLeagues
                      : t.filters.selectLeague
                }
                title={t.filters.league}
                disabled={!selectedSport || loadingLeagues}
                options={[
                  {
                    value: '',
                    label: !selectedSport
                      ? t.filters.selectSportFirst
                      : loadingLeagues
                        ? t.filters.loadingLeagues
                        : t.filters.selectLeague,
                  },
                  ...leagues.map((league) => ({
                    value: String(league.id),
                    label: translateEvent(league.name, t.eventsDictionary),
                  })),
                ]}
              />
            </div>

            <div className="manage-recs__filter-group manage-recs__filter-group--search">
              <label className="manage-recs__label">
                {t.manageRecommendations.searchTeams}
              </label>
              <input
                type="text"
                className="manage-recs__input"
                placeholder={t.manageRecommendations.searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={!selectedSport}
              />
            </div>
          </div>

          {selectedSport && (
            <div className="manage-recs__results">
              <h3 className="manage-recs__section-title">
                {t.manageRecommendations.games}
                {loadingGames && <span className="manage-recs__loading-inline"> ...</span>}
              </h3>

              {!loadingGames && games.length === 0 ? (
                <div className="manage-recs__empty">
                  {t.manageRecommendations.noGamesFound}
                </div>
              ) : (
                <div className="manage-recs__games-list">
                  {games.map((game) => (
                    <div
                      key={game.id}
                      className="manage-recs__game-row"
                      onClick={() => handleSelectGame(game)}
                    >
                      <div className="manage-recs__game-teams">
                        <span className="manage-recs__team">{translateEvent(game.home_team, t.eventsDictionary)}</span>
                        <span className="manage-recs__vs-small">vs</span>
                        <span className="manage-recs__team">{translateEvent(game.away_team, t.eventsDictionary)}</span>
                      </div>
                      <div className="manage-recs__game-details">
                        <span className="manage-recs__league-tag">{game.league?.name ? translateEvent(game.league.name, t.eventsDictionary) : ''}</span>
                        <span className="manage-recs__time-tag">{formatDateTime(game.datetime)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default AddRecommendation;
